import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute } from '@tanstack/react-router';
import { Eye, EyeOff, Upload, User as UserIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import z from 'zod';
import { passwordSchema } from '@/api/auth/auth.schema';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Content } from '@/components/ui/content';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group';
import { ContentLoader } from '@/components/ui/loaders';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useUploadFile } from '@/hooks/use-upload';
import { meta } from '@/lib/meta';
import { useChangePassword, useIsAuthenticated, useUpdateProfile } from '@/services/auth';

const profileFormSchema = z
  .object({
    name: z.string('Campo obrigatório!').min(1, 'Campo obrigatório!'),
    image: z.string().optional(),
    currentPassword: z.string().optional(),
    newPassword: z.string().optional(),
    confirmPassword: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.currentPassword && !data.newPassword && !data.confirmPassword) return;

    if (!data.currentPassword) {
      ctx.addIssue({
        code: 'custom',
        path: ['currentPassword'],
        message: 'Campo obrigatório!',
      });
    }

    const passwordCheck = passwordSchema.safeParse(data.newPassword);
    if (!passwordCheck.success) {
      ctx.addIssue({
        code: 'custom',
        path: ['newPassword'],
        message: passwordCheck.error.issues[0]?.message ?? 'Senha inválida!',
      });
    }

    if (data.newPassword !== data.confirmPassword) {
      ctx.addIssue({
        code: 'custom',
        path: ['confirmPassword'],
        message: 'As senhas não conferem!',
      });
    }
  });

type ProfileFormValues = z.infer<typeof profileFormSchema>;

export const Route = createFileRoute('/_app/profile')({
  head: () => meta({ label: 'Meu Perfil' }),
  component: RouteComponent,
});

function RouteComponent() {
  const { user } = useIsAuthenticated();
  const { mutate: updateProfile, isPending: isSavingProfile } = useUpdateProfile();
  const { mutate: changePassword, isPending: isSavingPassword } = useChangePassword();
  const { mutate: upload, isPending: isUploading } = useUploadFile();

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    shouldUseNativeValidation: false,
    defaultValues: {
      name: user?.name ?? '',
      image: user?.image ?? undefined,
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const isSubmitting = isSavingProfile || isSavingPassword;

  const clearPasswordFields = () => {
    form.setValue('currentPassword', '');
    form.setValue('newPassword', '');
    form.setValue('confirmPassword', '');
  };

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    upload(file, {
      onSuccess: (uploaded) => form.setValue('image', uploaded.path),
    });
  };

  const handleSubmit = (data: ProfileFormValues) => {
    updateProfile(
      { name: data.name, image: data.image },
      {
        onSuccess: () => {
          if (!data.newPassword || !data.currentPassword) return;

          changePassword(
            { currentPassword: data.currentPassword, newPassword: data.newPassword },
            { onSuccess: clearPasswordFields },
          );
        },
      },
    );
  };

  return (
    <Content
      breadcrumbs={{
        title: 'Meu Perfil',
        icon: 'user-round',
        items: [{ label: 'Meu Perfil', active: true }],
      }}
    >
      <ContentLoader show={isSubmitting} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Card de Avatar */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Foto de Perfil</CardTitle>
            <CardDescription>Atualize sua foto de perfil</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center space-y-4">
            <Avatar className="w-32 h-32">
              <AvatarImage src={form.watch('image')} />
              <AvatarFallback>
                <UserIcon className="w-16 h-16" />
              </AvatarFallback>
            </Avatar>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleAvatarChange}
              accept="image/*"
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              disabled={isUploading}
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2"
            >
              <Upload className="w-4 h-4" />
              {isUploading ? 'Enviando...' : 'Alterar Foto'}
            </Button>
          </CardContent>
        </Card>

        {/* Card de Dados Pessoais */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Informações Pessoais</CardTitle>
            <CardDescription>Atualize suas informações pessoais e senha</CardDescription>
          </CardHeader>
          <CardContent>
            <form noValidate id="profile-form" onSubmit={form.handleSubmit(handleSubmit)}>
              <FieldGroup>
                <FieldSet>
                  <FieldLegend>Dados Pessoais</FieldLegend>
                  <FieldDescription>Atualize seus dados pessoais.</FieldDescription>
                  <FieldGroup className="grid grid-cols-1 gap-4">
                    <Controller
                      name="name"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field aria-required data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="name">Nome</FieldLabel>
                          <Input
                            {...field}
                            id="name"
                            aria-invalid={fieldState.invalid}
                            placeholder="Seu nome completo"
                            autoComplete="name"
                          />
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />
                  </FieldGroup>
                </FieldSet>

                <FieldSet className="mt-8">
                  <FieldLegend>Alterar Senha</FieldLegend>
                  <FieldDescription>
                    Deixe em branco se não quiser alterar a senha.
                  </FieldDescription>
                  <FieldGroup className="grid grid-cols-1 gap-4">
                    <Controller
                      name="currentPassword"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="currentPassword">Senha Atual</FieldLabel>
                          <InputGroup>
                            <InputGroupInput
                              {...field}
                              id="currentPassword"
                              aria-invalid={fieldState.invalid}
                              placeholder="Digite sua senha atual"
                              type={showCurrentPassword ? 'text' : 'password'}
                              autoComplete="current-password"
                            />
                            <InputGroupAddon align="inline-end">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <InputGroupButton
                                    type="button"
                                    variant="ghost"
                                    aria-label="Mostrar/ocultar senha"
                                    size="icon-xs"
                                    onClick={() => setShowCurrentPassword((v) => !v)}
                                  >
                                    {showCurrentPassword ? <Eye /> : <EyeOff />}
                                  </InputGroupButton>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>
                                    {showCurrentPassword
                                      ? 'Ocultar senha'
                                      : 'Mostrar senha'}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </InputGroupAddon>
                          </InputGroup>
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />

                    <Controller
                      name="newPassword"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="newPassword">Nova Senha</FieldLabel>
                          <InputGroup>
                            <InputGroupInput
                              {...field}
                              id="newPassword"
                              aria-invalid={fieldState.invalid}
                              placeholder="Digite sua nova senha"
                              type={showNewPassword ? 'text' : 'password'}
                              autoComplete="new-password"
                            />
                            <InputGroupAddon align="inline-end">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <InputGroupButton
                                    type="button"
                                    variant="ghost"
                                    aria-label="Mostrar/ocultar senha"
                                    size="icon-xs"
                                    onClick={() => setShowNewPassword((v) => !v)}
                                  >
                                    {showNewPassword ? <Eye /> : <EyeOff />}
                                  </InputGroupButton>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>
                                    {showNewPassword ? 'Ocultar senha' : 'Mostrar senha'}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </InputGroupAddon>
                          </InputGroup>
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />

                    <Controller
                      name="confirmPassword"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="confirmPassword">
                            Confirmar Nova Senha
                          </FieldLabel>
                          <InputGroup>
                            <InputGroupInput
                              {...field}
                              id="confirmPassword"
                              aria-invalid={fieldState.invalid}
                              placeholder="Confirme sua nova senha"
                              type={showConfirmPassword ? 'text' : 'password'}
                              autoComplete="new-password"
                            />
                            <InputGroupAddon align="inline-end">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <InputGroupButton
                                    type="button"
                                    variant="ghost"
                                    aria-label="Mostrar/ocultar senha"
                                    size="icon-xs"
                                    onClick={() => setShowConfirmPassword((v) => !v)}
                                  >
                                    {showConfirmPassword ? <Eye /> : <EyeOff />}
                                  </InputGroupButton>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>
                                    {showConfirmPassword
                                      ? 'Ocultar senha'
                                      : 'Mostrar senha'}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </InputGroupAddon>
                          </InputGroup>
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />
                  </FieldGroup>
                </FieldSet>
              </FieldGroup>
            </form>
          </CardContent>
          <CardFooter>
            <Field className="flex justify-end gap-2" orientation="horizontal">
              <Button
                type="button"
                disabled={isSubmitting}
                variant="outline"
                onClick={() => form.reset()}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                variant="success"
                form="profile-form"
                className="flex items-center gap-2"
              >
                <Icon name="save" /> Salvar Alterações
              </Button>
            </Field>
          </CardFooter>
        </Card>
      </div>
    </Content>
  );
}
